import {NavButton} from "./NavButton";
import {executeLogoutFetch, setLogoutActive} from "../../../slices/loginFormSlice";
import React from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {AppModeSwitcher} from "../common/navigation/AppModeSwitcher";
import {DecisionDialog} from "../cms/dialog-windows/DecisionDialog";
import {LogoutIcon} from "../../icons/LogoutIcon";
import {BrandLogo} from "../../icons/BrandLogo";
import {USER_PROFILE} from "../../../utils/viewsConstants";
import {ReactSVG} from "react-svg";
import {setChosenLanguage, setNavPanelCollapsed} from "../../../slices/globalParamsSlice";
import {useWindowWidth} from "../../../hooks/useWindowWidth";
import {AppModeSwitcherWrapper} from "../common/navigation/AppModeSwitcherWrapper";
import {Tooltip} from "../cms/Tooltip";
import {languageStyles} from "../../../selectStyles";
import {CustomNoOptionsMessage} from "../cms/form-components/CustomNoOptionsMessage";
import Select from "react-select";
import {CustomSingleValueIcon} from "../cms/form-components/CustomSingleValueIcon";
import {NavPanelCollapsed} from "./NavPanelCollapsed";
import {NavPanelSkeleton} from "./NavPanelSkeleton";
import {useLanguageRefresh} from "../../../hooks/useLanguageRefresh";
import {useNavPanelDeps} from "../../../hooks/useNavPanelDeps";

export const NavPanel = ({children, childrenCollapsed, clearStateHandler}) => {
    const {t, i18n} = useTranslation();
    const dispatch = useDispatch();
    const {restaurant} = useSelector(state => state.dashboard.view);
    const {
        userData,
        isLoading: isUserProfileLoading
    } = useSelector(state => state.userProfile.getUserProfile);
    const {isLoading: isUserUpdatePending} = useSelector(state => state.userProfile.updateUserProfile);
    const {
        logoutActive,
        navPanelCollapsed,
        chosenLanguage,
        isLogoutHovered,
        setIsLogoutHovered,
        hasAccessToDashboard,
        handleSwitchView,
        languages
    } = useNavPanelDeps({clearStateHandler});
    const windowWidth = useWindowWidth();
    const isTablet = windowWidth < 1000;

    const handleLanguageChange = selected => {
        const lang = selected.value;
        dispatch(setChosenLanguage({
            value: lang,
            label: t(lang),
            collapsedLabel: t(`${lang}Short`)
        }));
        i18n.changeLanguage(lang);
    }

    useLanguageRefresh();

    if (isTablet) return null;

    if (!isUserUpdatePending && isUserProfileLoading) return (<NavPanelSkeleton/>);

    if (navPanelCollapsed) {
        return <NavPanelCollapsed childrenCollapsed={childrenCollapsed} clearStateHandler={clearStateHandler}/>
    }

    return (
        <div className={`app-nav-panel ${!hasAccessToDashboard && 'no-dashboard'}`}>
            {logoutActive &&
                <DecisionDialog
                    msg={t('confirmLogout')}
                    onCancel={() => dispatch(setLogoutActive(false))}
                    onSubmit={() => dispatch(executeLogoutFetch({isInactive: false}))}
                />
            }
            <div className={'app-nav-header'}>
                <div className={'nav-header-brand-group'}>
                    <BrandLogo/>
                </div>
                <div className={'flex-centered'}>
                    <Tooltip content={t('userProfile')} topOffset={20}>
                        <div className={'user-profile-logo'} onClick={() => handleSwitchView(USER_PROFILE)}>
                            {userData?.forename?.[0].toUpperCase()}
                        </div>
                    </Tooltip>
                </div>
            </div>

            <div className={'app-nav-welcome'}>
                <span className={'profile-name'}>
                    {t('welcome')} {userData?.forename}!
                </span>
            </div>

            <AppModeSwitcherWrapper hasAccessToDashboard={hasAccessToDashboard}>
                <AppModeSwitcher/>
            </AppModeSwitcherWrapper>

            <div className={'app-nav-menu'}>
                <ul className={'app-nav-ul'}>
                    {children}
                </ul>
            </div>

            <div className={'collapse-panel-button'}
                 onClick={() => dispatch(setNavPanelCollapsed(!navPanelCollapsed))}>
                <ReactSVG src={`${process.env.PUBLIC_URL}/theme/icons/chevron-left.svg`}/>
            </div>

            <div className={'app-nav-settings'}>
                <ul className={'app-nav-ul'}>
                    <div className={'app-nav-language-wrapper'}>
                        <Select id={'language'}
                                name={'language'}
                                styles={languageStyles}
                                value={chosenLanguage}
                                onChange={(selected) => handleLanguageChange(selected)}
                                placeholder={t('chooseLanguage')}
                                isClearable={false}
                                options={languages}
                                components={{
                                    NoOptionsMessage: CustomNoOptionsMessage,
                                    SingleValue: CustomSingleValueIcon
                                }}
                                closeMenuOnSelect={true}
                                menuPlacement={'top'}
                                isSearchable={false}
                                blurInputOnSelect={false}
                        />
                    </div>
                    <NavButton name={t('logout')}
                               icon={<LogoutIcon active={isLogoutHovered}/>}
                               onClick={() => dispatch(setLogoutActive(true))}
                               setHovered={setIsLogoutHovered}/>
                </ul>
            </div>
            <div className={'app-nav-restaurant-info-wrapper'}>
                <div className={'app-nav-restaurant-info-container'}>
                    <div className={'app-nav-restaurant-info'}>
                        <div>
                            <span className={'restaurant-info-name'}>{restaurant?.label}</span>
                            <span className={'restaurant-info-address'}>{restaurant?.value.address}</span>
                            <span className={'restaurant-info-address'}>{restaurant?.value.city}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className={'app-nav-footer'}>
                <span>Powered by HungryScan &copy;</span>
            </div>
        </div>
    );
}